import _ from "lodash";
import React from "react";
import moment from "moment";
import {
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_MYP,
  PROJECT_SUBTYPE_INTERNAL_ASSESSMENT,
  PROJECT_SUBTYPE_EXTERNAL_ASSESSMENT,
  PROJECT_SUBTYPE_SUBJECT_GROUP_PROJECT,
  WHOLE_SCHOOL_EVENTS,
  HOLIDAYS_AND_VACATIONS,
} from "Constants/stringConstants";

import {
  getOrganizationId,
  getAcademicYearEndDate,
} from "modules/CommonSelectors";
import { getIsAssessmentTypeConfigurationEnabled } from "Utils/ACLStoreUtils";
import {
  getStartEndTimeOfDateRange,
  isCurriculumProgramMYPOrDp,
  getCourseList,
  getProjectIconDetails,
  getFilteredUserCalendarClassPreference,
  getSelectedSchoolDetailsForCalendar,
} from "MyCalendar/modules/Utils";
import {
  getOrganizationDetailsFromCache,
  getOrganizationGlobalConstantsFromCache,
} from "modules/CommonGraphqlHelpers";
import { getIncrementedDate, getDateMomentObject } from "Utils/DateTimeHelpers";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";

import { MY_CALENDAR_CONSTANTS } from "MyCalendar/constants/stringConstants";

import {
  MYP_PROJECT_TAGS,
  DP_PROJECT_TAGS,
  CLASS_BOUND_CONTENT_TYPES,
} from "MyCalendar/modules/MyCalendarModule";
import { EXCLUDED_ASSESSMENT_TYPES } from "store/static";
import { PencilOutlined } from "@toddle-edu/ds-icons";
import { getGlobalTimezone, formatDate } from "Utils";
import { getAttendanceGlobalConstantsMemoize } from "AttendanceDashboard/modules/Utils";
import { ATTENDANCE_GLOBAL_CONSTANTS } from "modules/Services";
import { END_TYPES } from "../components/EventModalV2/context/RepeatEventContext";
import { getCalendarLocaleKey } from "./MyCalendarModule/RecurrenceLocaleKeyMappings";

const initialTagSelectedStyle = {
  marginTop: "3px", // this is intentially fine-tuned margin
  height: "12px",
  width: "12px",
  borderRadius: "50%",
};

export const DAYS = [
  {
    label: "Su",
    value: 0,
    textLabel: "Sun",
  },
  {
    label: "Mo",
    value: 1,
    textLabel: "Mon",
  },
  {
    label: "Tu",
    value: 2,
    textLabel: "Tue",
  },
  {
    label: "We",
    value: 3,
    textLabel: "Wed",
  },
  {
    label: "Th",
    value: 4,
    textLabel: "Thu",
  },
  {
    label: "Fr",
    value: 5,
    textLabel: "Fri",
  },
  {
    label: "Sa",
    value: 6,
    textLabel: "Sat",
  },
];

const getFilteredTags = tags => {
  return _.map(tags, tag => {
    const { id: value, label, uid } = tag;
    return {
      label,
      value,
      uid,
      icon: (
        <div
          style={{
            backgroundColor: _.get(tag, "colorCode", ""),
            ...initialTagSelectedStyle,
          }}
        ></div>
      ),
    };
  });
};

export const getFilteredTagsMemoize = _.memoize(
  params => getFilteredTags(params),
  params => JSON.stringify(params)
);

export const getFilteredEventTypes = ({
  tags,
  canCreateEventUsingAdminTag,
}) => {
  return _.chain(tags)
    .filter(tag => {
      return canCreateEventUsingAdminTag
        ? true
        : !_.includes([HOLIDAYS_AND_VACATIONS, WHOLE_SCHOOL_EVENTS], tag?.uid);
    })
    .map(tag => {
      const { id: value, label, uid } = tag;
      return {
        label: label,
        value,
        uid,
        icon: (
          <div
            style={{
              backgroundColor: _.get(tag, "colorCode", ""),
              width: "16px",
              height: "16px",
              borderRadius: "2px",
              margin: "2px",
            }}
          ></div>
        ),
      };
    })
    .value();
};

const getFilteredProjectTags = ({
  state = {},
  tags,
  projectGroups = [],
  currentCurriculumProgramType,
  personalProjectGroupId,
  communityProjectGroupId,
}) => {
  return _.reduce(
    tags,
    (tagList, { label, value }) => {
      if (
        currentCurriculumProgramType !== CURRICULUM_TYPE_MYP &&
        value === MY_CALENDAR_CONSTANTS.INTERNAL_EXTERNAL_ASSESSMENT &&
        !(
          _.find(projectGroups, {
            subType: PROJECT_SUBTYPE_EXTERNAL_ASSESSMENT,
          }) ||
          _.find(projectGroups, {
            subType: PROJECT_SUBTYPE_INTERNAL_ASSESSMENT,
          })
        )
      ) {
        return tagList;
      } else if (
        currentCurriculumProgramType !== CURRICULUM_TYPE_MYP &&
        value !== MY_CALENDAR_CONSTANTS.INTERNAL_EXTERNAL_ASSESSMENT
      ) {
        if (
          value === PROJECT_SUBTYPE_SUBJECT_GROUP_PROJECT &&
          !_.find(projectGroups, { subType: value })
        ) {
          return tagList;
        } else if (
          value !== PROJECT_SUBTYPE_SUBJECT_GROUP_PROJECT &&
          !_.find(projectGroups, { type: value })
        ) {
          return tagList;
        }
      }

      const projectGroupId = _.get(state, "myCalendar.projectGroupId", null);
      if (
        currentCurriculumProgramType === CURRICULUM_TYPE_MYP &&
        projectGroupId &&
        _.isEmpty(projectGroups)
      ) {
        if (
          value === MY_CALENDAR_CONSTANTS.PERSONAL &&
          personalProjectGroupId !== projectGroupId
        ) {
          return tagList;
        } else if (
          value === MY_CALENDAR_CONSTANTS.COMMUNITY &&
          communityProjectGroupId !== projectGroupId
        ) {
          return tagList;
        }
      } else if (
        currentCurriculumProgramType === CURRICULUM_TYPE_MYP &&
        !_.isEmpty(projectGroups) &&
        !_.find(projectGroups, { type: value })
      ) {
        return tagList;
      }

      const { backgroundColor } = getProjectIconDetails({ projectType: value });
      return [
        ...tagList,
        {
          label,
          value,
          icon: (
            <div
              style={{
                backgroundColor,
                ...initialTagSelectedStyle,
              }}
            ></div>
          ),
        },
      ];
    },
    []
  );
};

const getFilteredProjectTagsMemoize = _.memoize(
  params => getFilteredProjectTags(params),
  params => JSON.stringify(params)
);

export const getCurrentCurriculumProgramType = state => {
  const { currentCurriculumProgramType } = getSelectedSchoolDetailsForCalendar(
    state
  );
  return currentCurriculumProgramType || CURRICULUM_TYPE_PYP;
};

export const getUserProjectGroupsSelector = ({ queryData }) => {
  const projectGroups = _.get(queryData, "node.projectGroups", []);
  return {
    projectGroups,
  };
};

export const getAllProjectGroupsSelector = ({ queryData }) => {
  const allProjectGroups = _.get(queryData, "node.projectGroups", []);
  return {
    allProjectGroups,
  };
};

export const getTagList = (
  state,
  eventType,
  currentCurriculumProgramType,
  projectGroups,
  personalProjectGroupId,
  communityProjectGroupId,
  t
) => {
  switch (eventType) {
    case MY_CALENDAR_CONSTANTS.SCHOOL_CALENDAR_EVENT: {
      const tags = _.get(state, "myCalendar.schoolCalendarTags");
      return getFilteredTagsMemoize(tags);
    }
    default: {
      switch (currentCurriculumProgramType) {
        case CURRICULUM_TYPE_MYP:
          return getFilteredProjectTagsMemoize({
            tags: MYP_PROJECT_TAGS(t),
            state,
            projectGroups,
            currentCurriculumProgramType,
            personalProjectGroupId,
            communityProjectGroupId,
          });
        default:
          return getFilteredProjectTagsMemoize({
            tags: DP_PROJECT_TAGS(t),
            projectGroups,
            currentCurriculumProgramType,
          });
      }
    }
  }
};

export const getGradesWithCurriculam = (organizationId, eventType) => {
  const grades = _.get(
    getOrganizationDetailsFromCache(organizationId),
    "grades",
    []
  );
  switch (eventType) {
    case MY_CALENDAR_CONSTANTS.SCHOOL_CALENDAR_EVENT:
      return grades;
    default:
      return [];
  }
};

export const getPersonalProjectGroupId = state => {
  return _.get(state, "login.userInfo.personalProjectId", "");
};

export const getCommunityProjectGroupId = state => {
  return _.get(state, "login.userInfo.communityProjectId", "");
};

export const getCalendarEventsSelector = ({ queryData }) => {
  const calendarEvents = _.get(queryData, "calendarEvents", []);
  return { calendarEvents };
};

export const getMeetingBannerEventsSelector = ({ queryData }) => {
  const calendarEvents = _.get(queryData, "calendarEvents", []);

  const assignmentTypeEvents = _.filter(
    calendarEvents,
    calendarEvent => calendarEvent.itemType == "ASSIGNMENT"
  );
  const alterAssignmentTypeEvents = _.map(
    assignmentTypeEvents,
    assignmentTypeEvent => {
      return {
        ...assignmentTypeEvent.item,
        eventId: _.get(assignmentTypeEvent, "id", ""),
      };
    }
  );
  const calendarEventTypeEvents = _.filter(
    calendarEvents,
    calendarEvent => calendarEvent.itemType == "CALENDAR_EVENT_ITEM"
  );
  const alterCalendarEventTypeEvents = _.map(
    calendarEventTypeEvents,
    calendarEvent => {
      return {
        eventId: _.get(calendarEvent, "id", ""),
        content: {
          meeting: _.get(calendarEvent, "item.meeting", {}),
          assignmentTitle: _.get(calendarEvent, "item.label", ""),
          description: _.get(calendarEvent, "item.description", ""),
        },
        state: {
          publishedAt: _.get(calendarEvent, "startTime", ""),
        },
      };
    }
  );

  const calendarEventToShow = _.concat(
    alterAssignmentTypeEvents,
    alterCalendarEventTypeEvents
  );
  const totalStartingSoonAssignmentsCount = _.size(calendarEventToShow);

  return {
    startingSoonAssignments: calendarEventToShow,
    totalStartingSoonAssignmentsCount,
  };
};

export const getCalendarWidgetContentTypes = state => {
  return _.get(state, "teacher.widgetFilters.contentTypes", []);
};

export const getFilteredCalenderWidgetContentTypes = contentTypes => {
  return contentTypes.filter(event => {
    return _.includes(
      [
        MY_CALENDAR_CONSTANTS.SCHOOL_CALENDAR,
        MY_CALENDAR_CONSTANTS.RECURRING_MEETING,
      ],
      event
    );
  });
};

export const getCalendarEventsFilter = ({
  state,
  ownProps,
  startDate,
  endDate,
  calendarIds,
  academicYearId,
}) => {
  const { startDateTime, endDateTime } = getStartEndTimeOfDateRange({
    startDate,
    endDate,
  });
  const contentTypes = getCalendarWidgetContentTypes(state);
  const {
    currentCurriculumProgramType,
    currentCurriculumProgramId,
  } = getSelectedSchoolDetailsForCalendar(state);
  const userProjectGroupIds = _.get(ownProps, "userProjectGroupIds", []);

  const filteredUserProjectGroupIds = _.filter(
    userProjectGroupIds,
    userProjectGroupId => userProjectGroupId
  );

  return {
    type: ["COURSE", "PRIMARY"],
    calendarIds: !_.isEmpty(calendarIds) ? calendarIds : undefined,
    startDate: startDateTime,
    endDate: endDateTime,
    contentTypes,
    excludedAssessmentTypes: EXCLUDED_ASSESSMENT_TYPES,
    projectGroupIds: isCurriculumProgramMYPOrDp({
      currentCurriculumProgramType,
    })
      ? filteredUserProjectGroupIds
      : [],
    curriculumType: currentCurriculumProgramType,
    curriculumProgramId: currentCurriculumProgramId,
    academicYearId,
  };
};

export const getCalendarIds = ({
  userId,
  userEntityType,
  courses,
  currentCurriculumProgramId,
  calendarType,
}) => {
  const courseList = getCourseList({ courses });
  return getFilteredUserCalendarClassPreference({
    userId,
    courseList,
    userEntityType,
    currentCurriculumProgramId,
    calendarType,
  });
};

export const getCalendarWidgetStartEndDate = state => {
  const startDate = _.get(state, "teacher.widgetFilters.startDate");
  const endDate = getIncrementedDate({
    date: startDate,
    key: DATE_TIME_CONSTANTS.DAY,
    incrementBy: 7,
  });
  return { startDate, endDate };
};

export const getAssessmentTypes = (
  state,
  UbdAssessmentTypes,
  key = "myCalendar.assessmentTypes"
) => {
  const assessmentTypes = _.get(state, key, []);
  const assessmentTagListWithoutMeeting = _.filter(
    assessmentTypes,
    tag => tag !== "VIDEO_MEETING"
  );

  // When FF is on, tags are taskTypeIds — skip 'as' expansion
  if (getIsAssessmentTypeConfigurationEnabled()) {
    return assessmentTagListWithoutMeeting;
  }

  if (_.includes(assessmentTagListWithoutMeeting, "as")) {
    return _.uniq([
      ...assessmentTagListWithoutMeeting,
      ..._.filter(
        _.keys(UbdAssessmentTypes),
        type => !_.includes(["qt", "le"], type)
      ),
    ]);
  }
  return assessmentTagListWithoutMeeting;
};

export const getCalendarOpenedLocation = ownProps => {
  const params = _.get(ownProps, "params", {});
  if (params?.projectGroup) {
    return "deadline";
  }
  if (params?.course_id) {
    return "inside_classroom";
  }
  return "home";
};

const getProjectGroupsByUser = ({
  projectGroupId,
  courseId,
  projectGroups,
  courseProjectGroups,
  isAdmin,
}) => {
  if (projectGroupId) {
    return _.filter(
      projectGroups,
      projectGroup => projectGroup?.id === projectGroupId
    );
  }

  if (courseId) {
    if (!_.isEmpty(courseProjectGroups)) {
      return courseProjectGroups;
    }
    return [];
  }

  if (!isAdmin) {
    return _.filter(projectGroups, projectGroup =>
      _.includes(
        [
          PROJECT_SUBTYPE_EXTERNAL_ASSESSMENT,
          PROJECT_SUBTYPE_INTERNAL_ASSESSMENT,
        ],
        projectGroup?.subType
      )
    );
  }
  return _.filter(projectGroups, project => !_.isEmpty(project));
};

const getProjectGroupsByUserMemoized = _.memoize(
  params => getProjectGroupsByUser(params),
  params => JSON.stringify(params)
);

export const getProjectGroups = (state, isAdmin) => {
  const projectGroupId = _.get(state, "myCalendar.projectGroupId", null);
  const courseId = _.get(state, "myCalendar.courseId", null);
  const projectGroups = _.get(state, "myCalendar.projectGroups", []);
  const courseProjectGroups = _.get(
    state,
    "myCalendar.courseProjectGroups",
    []
  );

  return getProjectGroupsByUserMemoized({
    projectGroupId,
    courseId,
    projectGroups,
    courseProjectGroups,
    isAdmin,
  });
};

// This will return projectGroupId from /deadlines route if not opened from classRoom
export const getProjectGroupId = ownProps => {
  return _.get(ownProps, "params.projectGroup", null);
};

export const getSchoolCalendarTagIds = state => {
  const schoolCalendarTagIds = _.get(
    state,
    "myCalendar.schoolCalendarTagIds",
    []
  );
  return _.filter(
    schoolCalendarTagIds,
    tagId => !_.startsWith(tagId, "NEW", 0)
  );
};

export const getListViewFilters = ({
  state,
  ownProps,
  startDateTime,
  endDateTime,
  courseId,
  calendarIds,
  academicYearId,
}) => {
  const projectGroupId = _.get(ownProps, "projectGroupId");
  const projectGroupIds = projectGroupId ? [projectGroupId] : [];

  return {
    type: ["COURSE", "PRIMARY"],
    startDate: startDateTime,
    endDate: endDateTime,
    contentTypes: [MY_CALENDAR_CONSTANTS.PROJECT_DEADLINE_EVENT],
    curriculumType: getCurrentCurriculumProgramType(state),
    curriculumProgramId: getSelectedSchoolDetailsForCalendar(state)
      .currentCurriculumProgramId,
    projectGroupIds,
    calendarIds: !_.isEmpty(courseId) ? calendarIds : undefined,
    academicYearId,
  };
};

export const getContentTypes = ({ state, calendarEventIds }) => {
  const contentTypes = _.get(state, "myCalendar.contentTypes", {});
  const filteredContentTypes = _.filter(
    contentTypes,
    type => !_.includes(CLASS_BOUND_CONTENT_TYPES, type)
  );
  return _.isEmpty(calendarEventIds) ? filteredContentTypes : contentTypes;
};

export const getCurrentDateObject = onEndDate => {
  const momentDate = getDateMomentObject(onEndDate);
  const dayName = momentDate.format("dddd");
  const dayNumber = momentDate.date();
  const weekOfMonth = Math.ceil(dayNumber / 7);
  const day = momentDate.day();
  return { day, dayName, dayNumber, weekOfMonth };
};

export const getOrdinalSuffix = weekOfMonth => {
  const ordinals = ["first", "second", "third", "fourth", "fifth"];
  return ordinals[weekOfMonth - 1];
};

export const getTabs = ({ t, eventStartDate }) => {
  const { dayName, dayNumber, weekOfMonth } = getCurrentDateObject(
    eventStartDate
  );
  return [
    {
      value: "DAY",
      key: "DAY",
      label: t("calendar:monthly_on_day_label", {
        label: dayNumber,
      }),
    },
    {
      value: "WEEK",
      key: "WEEK",
      label: t("calendar:monthly_on_week_label", {
        label:
          weekOfMonth === 5
            ? t("calendar:last")
            : getOrdinalSuffix(weekOfMonth),
        week: dayName,
      }),
    },
  ];
};

// Helper function to get ordinal suffix
export const getOrdinal = num => {
  if (num >= 11 && num <= 13) return "th";
  switch (num % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

function isLastOccurrenceOfWeekday(date) {
  const mDate = moment(date);
  return mDate.clone().add(7, "days").month() !== mDate.month();
}

export const getRepeatEventOptions = ({
  date,
  t,
  curriculumProgramId,
  currentAcademicYearId,
  repeatEventSettings = null,
}) => {
  const momentDate = getDateMomentObject(date || new Date());
  const dayName = momentDate.format("dddd");
  const dayNumber = momentDate.date();
  const weekOfMonth = Math.ceil(dayNumber / 7);

  const dayOrdinal = getOrdinal(dayNumber);
  const weekOrdinal = getOrdinal(weekOfMonth);
  const organizationId = getOrganizationId();
  const timezone = getGlobalTimezone();

  // Q. why !repeatEventSettings.endDate check?
  // A. !repeatEventSettings.endDate checks added for count based occourences endDate will be null and
  // repeatEventSettings  will not be empty object in that case and end date will be invalid to avoid this we have added this check
  const endDate =
    _.isEmpty(repeatEventSettings) || !repeatEventSettings.endDate
      ? getAcademicYearEndDate({ organizationId })
      : getDateMomentObject(repeatEventSettings.endDate);

  const data = getOrganizationGlobalConstantsFromCache({
    organizationId,
    filters: {
      academicYearIds: currentAcademicYearId,
      names: [ATTENDANCE_GLOBAL_CONSTANTS.OPERATIONAL_DAYS],
    },
  });
  const globalConstants = _.map(
    _.get(data, "node.globalConstants", []),
    constant => ({
      ...constant,
      value: !_.isNil(constant.value) ? JSON.parse(constant.value) : null,
    })
  );
  const { operationalWeekDays } = getAttendanceGlobalConstantsMemoize({
    globalConstants,
    curriculumProgramId,
  });

  const { customLabelText } = getSummaryText({
    t,
    state: {
      ...repeatEventSettings,
      academicYearEndDate: endDate,
      repeatType: repeatEventSettings?.frequency,
    },
    date,
  });
  return _.filter(
    [
      {
        id: "school_day",
        label: t("calendar:event_school_day"),
        value: {
          id: "school_day",
          frequency: "WEEKLY",
          weekDays: operationalWeekDays,
          interval: 1,
          monthDays: [],
          setPositions: [],
          timezone,
          endDate,
        },
      },
      {
        id: "weekly",
        label: t("calendar:event_weekly_on", { dayName }),
        value: {
          id: "weekly",
          frequency: "WEEKLY",
          weekDays: [momentDate.day()],
          interval: 1,
          monthDays: [],
          setPositions: [],
          timezone,
          endDate,
        },
      },
      {
        id: "every_2_weeks",
        label: t("calendar:event_every_2_weeks_on", { dayName }),
        value: {
          id: "every_2_weeks",
          frequency: "WEEKLY",
          weekDays: [momentDate.day()],
          interval: 2,
          monthDays: [],
          setPositions: [],
          timezone,
          endDate,
        },
      },
      {
        id: "monthly_by_date",
        label: t("calendar:event_every_nth_day", {
          day: dayNumber,
          ordinal: dayOrdinal,
        }),
        value: {
          id: "monthly_by_date",
          frequency: "MONTHLY",
          weekDays: [],
          interval: 1,
          monthDays: [dayNumber],
          setPositions: [],
          timezone,
          endDate,
        },
      },
      {
        ...(weekOfMonth !== 5
          ? {
              id: "monthly_by_day",
              label: t("calendar:event_every_nth_weekday", {
                week: weekOfMonth,
                ordinal: weekOrdinal,
                dayName,
              }),
              value: {
                id: "monthly_by_day",
                frequency: "MONTHLY",
                weekDays: [momentDate.day()],
                interval: 1,
                monthDays: [],
                setPositions: [weekOfMonth],
                timezone,
                endDate,
              },
            }
          : {
              id: "monthly_by_day",
              label: t("calendar:event_every_last_weekday", {
                dayName,
              }),
              value: {
                id: "monthly_by_day",
                frequency: "MONTHLY",
                weekDays: [momentDate.day()],
                interval: 1,
                monthDays: [],
                setPositions: [-1], // -1 indicates last occurrence of the weekday in the month
                timezone,
                endDate,
              },
            }),
      },
      {
        ...(repeatEventSettings && repeatEventSettings?.id === "custom"
          ? {
              id: "custom",
              label: customLabelText,
              value: { ...repeatEventSettings, id: "custom", timezone },
            }
          : {}),
      },
      {
        id: "custom",
        label: t("calendar:event_custom"),
        icon: <PencilOutlined variant="outlined" size="small" />,
        value: {
          id: "custom",
        },
      },
    ],
    option => !_.isEmpty(option)
  );
};

const formatSelectedDays = selectedDays => {
  if (_.isEmpty(selectedDays)) return "";
  const sorted = _.sortBy(selectedDays); // sort days
  const labels = sorted.map(v => _.find(DAYS, { value: v })?.textLabel);
  const isContinuous = _.every(
    sorted,
    (val, i, arr) => i === 0 || val === arr[i - 1] + 1
  );
  if (isContinuous && sorted.length > 1) {
    return `${_.first(labels)}-${_.last(labels)}`;
  }
  return labels.join(", ");
};

export const getSummaryText = ({ t, state, date, useNewLocale = false }) => {
  const {
    interval,
    repeatType,
    endType,
    endDate,
    occurrences,
    academicYearEndDate,
    weekDays,
    monthDays,
  } = state;
  const { dayName, dayNumber, weekOfMonth } = getCurrentDateObject(date);
  const academicYearFormattedEndDate = formatDate({
    date: academicYearEndDate,
    format: "DD MMM YYYY",
  });
  const onEndDateFormat = formatDate({ date: endDate, format: "DD MMM YYYY" });
  const getRecurrenceLocaleKey = key => {
    return getCalendarLocaleKey(key, useNewLocale);
  };
  switch (repeatType) {
    case "DAILY": {
      const label =
        interval === 1
          ? t(getRecurrenceLocaleKey("day"))
          : `${interval} ${t(getRecurrenceLocaleKey("day_unit"))}`;

      const customLabelText =
        interval === 1
          ? t(getRecurrenceLocaleKey("repeat_every_day"))
          : t(getRecurrenceLocaleKey("event_every_count_days"), {
              count: interval,
            });

      switch (endType) {
        case END_TYPES.ACADEMIC_YEAR:
          return {
            subtext: t(
              getRecurrenceLocaleKey(
                "repeats_every_count_until_end_academic_year"
              ),
              {
                label,
                endDate: formatDate({ date: endDate, format: "DD MMM YYYY" }),
              }
            ),
            customLabelText,
          };

        case END_TYPES.ON_DATE:
          return {
            subtext: t(
              getRecurrenceLocaleKey("repeats_every_count_until_date"),
              {
                label,
                endDate: formatDate({ date: endDate, format: "DD MMM YYYY" }),
              }
            ),
            customLabelText,
          };

        case END_TYPES.AFTER_OCCURRENCES:
          return {
            subtext: t(
              occurrences > 1
                ? getRecurrenceLocaleKey(
                    "repeats_every_count_ends_after_occurrences"
                  )
                : getRecurrenceLocaleKey(
                    "repeats_every_count_ends_after_occurrence"
                  ),
              {
                label,
                occurrences,
              }
            ),
            customLabelText,
          };

        default:
          return { subtext: "", customLabelText };
      }
    }

    case "WEEKLY": {
      const label =
        interval === 1
          ? t(getRecurrenceLocaleKey("week"))
          : `${interval} ${t(getRecurrenceLocaleKey("week_unit"))}`;
      const day = formatSelectedDays(weekDays);

      const customLabelText =
        interval === 1
          ? t(getRecurrenceLocaleKey("repeat_every_weeks_on_day"), {
              dayName: day,
            })
          : t(getRecurrenceLocaleKey("event_every_count_weeks_on"), {
              count: interval,
              dayName: day,
            });

      switch (endType) {
        case END_TYPES.ACADEMIC_YEAR:
          return {
            subtext: t(
              getRecurrenceLocaleKey("repeats_every_count_on_day_until_date"),
              {
                label,
                day,
                endDate: academicYearFormattedEndDate,
              }
            ),
            customLabelText,
          };

        case END_TYPES.ON_DATE:
          return {
            subtext: t(
              getRecurrenceLocaleKey("repeats_every_count_on_day_until_date"),
              {
                label,
                day,
                endDate: onEndDateFormat,
              }
            ),
            customLabelText,
          };

        case END_TYPES.AFTER_OCCURRENCES:
          return {
            subtext: t(
              occurrences > 1
                ? getRecurrenceLocaleKey(
                    "repeats_every_count_on_day_ends_after_occurrences"
                  )
                : getRecurrenceLocaleKey(
                    "repeats_every_count_on_day_ends_after_occurrence"
                  ),
              { label, day, occurrences }
            ),
            customLabelText: t(
              getRecurrenceLocaleKey("repeat_every_weeks_day"),
              {
                dayName: day,
              }
            ),
          };

        default:
          return { subtext: "", customLabelText };
      }
    }

    case "MONTHLY": {
      const label =
        interval === 1
          ? t(getRecurrenceLocaleKey("month"))
          : `${interval} ${t(getRecurrenceLocaleKey("month_unit"))}`;
      const weekName =
        weekOfMonth === 5
          ? t(getRecurrenceLocaleKey("last"))
          : getOrdinalSuffix(weekOfMonth);
      const ordinal = getOrdinal(dayNumber);
      const month = _.isEmpty(monthDays)
        ? `${weekName} ${dayName}`
        : `${dayNumber}${ordinal}`;

      const customLabelMonthText = _.isEmpty(monthDays)
        ? `${weekName} ${dayName}`
        : `${t(getRecurrenceLocaleKey("day"))} ${dayNumber}${ordinal} `;

      const customLabelText =
        interval === 1
          ? t(getRecurrenceLocaleKey("repeat_every_months_on_day_count"), {
              count: customLabelMonthText,
            })
          : t(getRecurrenceLocaleKey("event_every_count_months_on_day"), {
              count: interval,
              day: customLabelMonthText,
            });

      switch (endType) {
        case END_TYPES.ACADEMIC_YEAR:
          return {
            subtext: t(
              getRecurrenceLocaleKey(
                "repeats_every_count_on_month_until_end_academic_year"
              ),
              {
                label,
                month,
              }
            ),
            customLabelText,
          };

        case END_TYPES.ON_DATE:
          return {
            subtext: t(
              getRecurrenceLocaleKey("repeats_every_count_on_month_until_date"),
              {
                label,
                month,
                endDate: onEndDateFormat,
              }
            ),
            customLabelText,
          };

        case END_TYPES.AFTER_OCCURRENCES:
          return {
            subtext: t(
              occurrences > 1
                ? getRecurrenceLocaleKey(
                    "repeats_every_count_on_month_ends_after_occurrences"
                  )
                : getRecurrenceLocaleKey(
                    "repeats_every_count_on_month_ends_after_occurrence"
                  ),
              { label, month, occurrences }
            ),
            customLabelText,
          };

        default:
          return { subtext: "", customLabelText };
      }
    }

    default:
      return { subtext: "", customLabelText: "" };
  }
};

/**
 * @note When adding or modifying locale keys, always use `getCalendarLocaleKey()` helper function
 * to we keep this complex logic maintainable.
 */
export const getSubText = ({
  t,
  repeatEventSettings,
  date,
  isVideoMeeting,
}) => {
  const {
    id,
    endDate,
    frequency,
    interval,
    monthDays,
    setPositions,
    weekDays,
    count,
    endType,
  } = repeatEventSettings || {};
  const organizationId = getOrganizationId();
  const academicYearEndDate = getAcademicYearEndDate({ organizationId });
  const formattedEndDate = formatDate({
    date: endDate,
    format: DATE_TIME_CONSTANTS.DD_MMM_YYYY,
  });
  const momentDate = moment(date);
  const dayName = momentDate.format("dddd");
  const dayNumber = momentDate.date();
  const weekOfMonth = Math.ceil(dayNumber / 7);
  const dayOrdinal = getOrdinal(dayNumber);
  const weekOrdinal = getOrdinal(weekOfMonth);

  const getRecurrenceLocaleKey = key => {
    return getCalendarLocaleKey(key, isVideoMeeting);
  };

  switch (id) {
    case "school_day": {
      return t(getRecurrenceLocaleKey("repeats_every_label_until_end"), {
        label: t(getRecurrenceLocaleKey("school_day")),
        academicYearEndDate: formattedEndDate,
      });
    }
    case "weekly": {
      return t(getRecurrenceLocaleKey("repeats_every_label_until_end"), {
        label: t(getRecurrenceLocaleKey("week_on_label"), { dayName }),
        academicYearEndDate: formattedEndDate,
      });
    }
    case "every_2_weeks": {
      return t(getRecurrenceLocaleKey("repeats_every_label_until_end"), {
        label: t(getRecurrenceLocaleKey("event_two_weeks_on_label"), {
          dayName,
        }),
        academicYearEndDate: formattedEndDate,
      });
    }
    case "monthly_by_date": {
      return t(getRecurrenceLocaleKey("repeats_on_label_until_end"), {
        label: t(getRecurrenceLocaleKey("date_of_month"), {
          day: dayNumber,
          ordinal: dayOrdinal,
        }),
        academicYearEndDate: formattedEndDate,
      });
    }
    case "monthly_by_day": {
      return t(getRecurrenceLocaleKey("repeats_on_label_until_end"), {
        label:
          weekOfMonth !== 5
            ? t(getRecurrenceLocaleKey("day_of_month"), {
                week: weekOfMonth,
                ordinal: weekOrdinal,
                dayName,
              })
            : t(getRecurrenceLocaleKey("event_every_last"), {
                dayName,
              }),
        academicYearEndDate: formattedEndDate,
      });
    }
    case "custom": {
      const customState = {
        interval,
        repeatType: frequency,
        endType,
        endDate,
        occurrences: count,
        weekDays,
        monthDays,
        setPositions,
        academicYearEndDate,
      };
      return _.get(
        getSummaryText({
          t,
          state: customState,
          date,
          useNewLocale: isVideoMeeting,
        }),
        "subtext"
      );
    }
    default:
      return "";
  }
};

export const getErrorText = ({
  t,
  state,
  operationalDays,
  skipNonOperationalDays,
}) => {
  const DAY_ARRAY = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  if (!skipNonOperationalDays) return "";

  const { weekDays } = state;
  const nonOperationalDays = _.filter(
    weekDays,
    day => !_.includes(JSON.parse(operationalDays), day)
  );
  const disableDoneButton =
    _.size(nonOperationalDays) === _.size(weekDays) &&
    _.size(nonOperationalDays) > 0;
  const dayName = _.map(nonOperationalDays.sort(), day =>
    _.get(DAY_ARRAY, day)
  ).join(", ");
  const errorText = !_.isEmpty(nonOperationalDays)
    ? t(getCalendarLocaleKey("error_text"), {
        dayName,
      })
    : "";
  return { errorText, disableDoneButton };
};

export const getIsRecurrenceInvalid = ({
  state,
  operationalDays,
  skipNonOperationalDays,
}) => {
  if (!skipNonOperationalDays) return false;

  const { weekDays } = state;
  const nonOperationalDays = _.filter(
    weekDays,
    day => !_.includes(JSON.parse(operationalDays), day)
  );
  const disableDoneButton =
    _.size(nonOperationalDays) === _.size(weekDays) &&
    _.size(nonOperationalDays) > 0;
  return disableDoneButton;
};
